import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ConvexMessage } from '../types/message';
import { useAuth } from '../contexts/AuthContext';

interface UseMessageGroupsResult {
  globalBranchCount: number;
  setGlobalBranchCount: (newCount: number) => void;
  activeBranches: Map<string, number>;
  setActiveBranchForPair: (messagePairId: string, branchId: number) => void;
  pairGroups: Map<string, number>;
  branchesMap: Map<string, number[]>;
  filteredMessages: ConvexMessage[];
  maxBranchId: number;
}

/**
 * Custom hook to handle message groups and branch processing logic
 * @param messages - Array of messages to process
 * @param chatId - Current chat ID
 * @returns Object containing globalBranchCount, setGlobalBranchCount, activeBranches, setActiveBranchForPair, pairGroups, branchesMap, filteredMessages, and maxBranchId
 */
export const useMessageGroups = (
  messages: ConvexMessage[],
  chatId?: string | null
): UseMessageGroupsResult => {
  const auth = useAuth();
  // Internal state for global branch count
  const [globalBranchCountInternal, setGlobalBranchCountInternal] = useState<number>(1);
  const [activeBranches, setActiveBranches] = useState<Map<string, number>>(new Map());
  const isInitialized = useRef(false);

  // Calculate the maximum branch ID from all messages
  const maxBranchId = useMemo(() => {
    if (!messages.length || messages.length === 0) {
      return 1;
    }
    const max = Math.max(...messages.map((msg) => msg.branchId || 1));
    return max;
  }, [messages]);

  const mostRecentBranchCount = useMemo(() => {
    if (!messages.length) {
      return globalBranchCountInternal;
    }
    // Sort messages by timestamp in descending order and get the most recent branchId
    const sortedMessages = [...messages].sort((a, b) => {
      const timeA = a.timestamp ? a.timestamp : 0;
      const timeB = b.timestamp ? b.timestamp : 0;
      return timeB - timeA;
    });
    const branchId = sortedMessages[0]?.branchId || globalBranchCountInternal;
    return branchId;
  }, [messages, globalBranchCountInternal]);

  // Need to detect when messages first arrive
  const messagesRef = useRef<ConvexMessage[]>([]);
  
  useEffect(() => {
    // Only run on chatId changes and initial message load
    if (isInitialized.current && chatId === messagesRef.current?.[0]?.chatId) return;
    
    const isGenerating = messages.some(msg => msg.isGenerating);
    if(isGenerating) return;
    
    // Process messages if they're available and we haven't initialized yet
    if (messages.length > 0 && !auth.isDataLoading && chatId) {
      // Save current messages for reference
      messagesRef.current = messages;
      
      const newBranchCount = Math.max(mostRecentBranchCount, maxBranchId);
      setGlobalBranchCountInternal(newBranchCount);
      isInitialized.current = true;
    } 
    // Handle the case where we initialize with no messages
    else if (!messages.length && !auth.isDataLoading && chatId && !isInitialized.current) {
      setGlobalBranchCountInternal(1);
      isInitialized.current = true;
    }
  }, [chatId, mostRecentBranchCount, maxBranchId, auth.isDataLoading, messages]);

  // Reset initialization when chatId changes
  useEffect(() => {
    isInitialized.current = false;
  }, [chatId]);

  const setActiveBranchForPair = useCallback((messagePairId: string, branchId: number) => {
    setActiveBranches(prev => {
      const newMap = new Map(prev);
      newMap.set(messagePairId, branchId);
      return newMap;
    });
  }, []);

  // Precompute message pair groups and branches map for efficiency
  const { pairGroups, branchesMap } = useMemo(() => {
    const pairGroups = new Map<string, number>();
    const branchesMap = new Map<string, number[]>();
    let groupCounter = 1;

    messages.forEach(message => {
      const pairId = message.messagePairId || '';
      if (!pairId) return; // Skip empty pairIds
      // Assign group numbers to message pairs
      if (!pairGroups.has(pairId)) {
        pairGroups.set(pairId, groupCounter++);
      }
      // Build branches map
      const key = `${pairId}-${message.isAi}`;
      const branches = branchesMap.get(key) || [];
      const branchId = message.branchId;
      if (branchId !== undefined && !branches.includes(branchId)) {
        branches.push(branchId);
      }
      branchesMap.set(key, branches);
    });
    return { pairGroups, branchesMap };
  }, [messages]);

  // Filter messages with skip-and-carry logic
  const filteredMessages = useMemo(() => {
    if (!messages.length) {
      return [];
    }

    const pairIdOrder = Array.from(pairGroups.keys());
    const result: ConvexMessage[] = [];
    let carriedMinBranch = 1; // Starts at 1, carries downward

    for (const pairId of pairIdOrder) {
      const groupMsgs = messages.filter(msg => msg.messagePairId === pairId);
      if (groupMsgs.length === 0) {
        continue; // Skip empty pairs, carry min unchanged
      }

      const selectedBranchIdForPair = activeBranches.get(pairId) || globalBranchCountInternal;
      const currentMinBranch = carriedMinBranch; // Capture variable in loop scope

      // Evaluate user message: max <= selectedBranchIdForPair and >= carriedMinBranch (no fallback)
      const userMessagesInPair = groupMsgs.filter(msg => !msg.isAi && (msg.branchId || 1) <= selectedBranchIdForPair && (msg.branchId || 1) >= currentMinBranch)
                                          .sort((a, b) => (b.branchId || 1) - (a.branchId || 1));
      const selectedUserMessage = userMessagesInPair[0] || null;
      if (!selectedUserMessage) {
        continue; // Skip this pair, carry min unchanged
      }
      result.push(selectedUserMessage);
      const userMsgBranchId = selectedUserMessage.branchId || 1;

      // Evaluate AI: max <= selectedBranchIdForPair and >= userMsgBranchId (no fallback)
      let selectedAiMessage: ConvexMessage | null = null;
      if (activeBranches.has(pairId)) {
        const targetAiBranchId = selectedBranchIdForPair;
        const aiMessagesInPair = groupMsgs.filter(msg => msg.isAi && (msg.branchId || 1) <= targetAiBranchId && (msg.branchId || 1) >= userMsgBranchId)
                                          .sort((a, b) => (b.branchId || 1) - (a.branchId || 1));
        selectedAiMessage = aiMessagesInPair[0] || null;
      } else {
        const aiMessagesAtQualifier = groupMsgs.filter(msg => 
          msg.isAi && 
          (msg.branchId || 1) <= globalBranchCountInternal &&
          (msg.branchId || 1) >= userMsgBranchId
        ).sort((a, b) => (b.branchId || 1) - (a.branchId || 1));
        selectedAiMessage = aiMessagesAtQualifier[0] || null;
      }

      if (selectedAiMessage) {
        result.push(selectedAiMessage);
        carriedMinBranch = selectedAiMessage.branchId || 1; // Carry AI branch as min
      } else {
        carriedMinBranch = userMsgBranchId; // Carry user branch as min
      }
    }

    return result;
  }, [messages, globalBranchCountInternal, pairGroups, activeBranches]);

  // Wrap the setter to clear active branch overrides on global change
  const setGlobalBranchCount = useCallback((newCount: number) => {
    setActiveBranches(new Map());
    setGlobalBranchCountInternal(newCount);
  }, []);


  return {
    globalBranchCount: globalBranchCountInternal,
    setGlobalBranchCount,
    activeBranches,
    setActiveBranchForPair,
    pairGroups,
    branchesMap,
    filteredMessages,
    maxBranchId
  };
}; 